import { addDays, differenceInCalendarDays, differenceInDays } from 'date-fns';
import { getEvaluationTimesSummary } from 'domains/calendars/queries';
import { useQuery, UseQueryResult } from 'react-query';

import { formatInTimeZone } from 'date-fns-tz';
import { EvaluationDuration, EvaluationTimeSlotSummary, SmallTherapist } from 'domains/calendars/types';
import { sortBy, uniqBy } from 'lodash';

/** Should prevent times from jumping days due to offset */
const HELPER_TIMEZONE = 'America/Chicago'; // Central Time (CT), middle of the USA

export type UseSummaryCalendarParams = {
  fromDate: Date;
  toDate: Date;
  duration: EvaluationDuration;
  therapistID?: string;
  clientID?: string;
  appointmentID?: string;
} & Partial<{ detectedAreas: string[]; insuranceName: string; state: string }>;

export type EvaluationCalendarSummary = {
  summaryData: EvaluationCalendarDay[];
  totalTimeSlots: number;
};

export type EvaluationCalendarSummaryQuery = UseQueryResult<EvaluationCalendarSummary>;

export const useEvaluationCalendarSummary = (params: UseSummaryCalendarParams): EvaluationCalendarSummaryQuery => {
  const { detectedAreas, insuranceName, state, duration, therapistID, appointmentID, clientID } = params;

  const fromDate = formatInTimeZone(params.fromDate, HELPER_TIMEZONE, 'yyyy-MM-dd');
  const toDate = formatInTimeZone(params.toDate, HELPER_TIMEZONE, 'yyyy-MM-dd');
  const specialties = detectedAreas?.join(',');

  return useQuery(
    ['eval-available-times', fromDate, insuranceName, specialties, state, toDate, duration, therapistID, appointmentID, clientID],
    () =>
      getEvaluationTimesSummary({
        fromDate,
        toDate,
        insuranceName,
        specialties,
        state,
        duration,
        therapistID: therapistID ?? undefined,
        appointmentID: appointmentID ?? undefined,
        clientID: clientID ?? undefined,
      }),
    {
      select: data => {
        const processedData = processSummaryData(params.fromDate, params.toDate, data);
        return {
          summaryData: processedData,
          totalTimeSlots: Object.values(processedData).reduce((acc, day) => acc + day.timeSlotCount, 0),
        };
      },
    },
  );
};

export type EvaluationTimeSlot = {
  time: Date;

  calendarID: string;
  therapistName: string;
  therapistID: string;
  /** Manually formed ID to help identify one slot from another in Select component or elsewhere */
  id: string;
};

export type TherapistAvailability = {
  therapist: SmallTherapist;
  therapistName: string;
  timeSlots: EvaluationTimeSlot[];
};

export type EvaluationCalendarDay = {
  day: Date;

  /** Therapists that contain availability on this day */
  availableTherapists: SmallTherapist[];
  /** How many time slots are available on this day */
  timeSlotCount: number;
  /** How many therapists are available on this day */
  therapistCount: number;
  /** Evaluation availability grouped by therapist */
  availabilityByTherapist: TherapistAvailability[];
  /** Time slots available on this day, duplicates from two therapists filtered out */
  timeSlots: EvaluationTimeSlot[];
};

/**
 * Grabs raw summary data and transforms it into a more ergonomic format
 */
const processSummaryData = (from: Date, to: Date, data: EvaluationTimeSlotSummary): EvaluationCalendarDay[] => {
  // Initialize result map
  const resultMap: EvaluationCalendarDay[] = [];

  for (let index = 0; index < differenceInDays(to, from); index++) {
    const currentDate = addDays(from, index);
    resultMap[index] = {
      day: currentDate,
      timeSlotCount: 0,
      therapistCount: 0,
      availableTherapists: [],
      availabilityByTherapist: [],
      timeSlots: [],
    };
  }

  data.times.forEach(({ time, therapistEmails }) => {
    const daysDifference = differenceInCalendarDays(time, from);
    if (resultMap[daysDifference]) {
      const rawTherapists = resultMap[daysDifference].availableTherapists;

      resultMap[daysDifference].timeSlotCount++;

      therapistEmails.forEach((therapistEmail, therapistIndex) => {
        const therapist = data.therapists.find(t => t.therapistID === therapistEmail);

        if (!therapist) {
          throw new Error(`Bad evaluation calendar data, Therapist not found for email ${therapistEmails[0]}`);
        }

        const slot: EvaluationTimeSlot = {
          id: `${therapist.therapistID}-${time.toISOString()}`,
          time,
          therapistID: therapist.therapistID,

          calendarID: therapist.acuityCalendarID,
          therapistName: `${therapist.firstName} ${therapist.lastName}`,
        };

        if (therapistIndex === 0) {
          // Only push on index 0, in order to not duplicate this slot for contexts where we don't care about the therapist owner of the slot @TODO refactor
          resultMap[daysDifference].timeSlots.push(slot);
        }

        rawTherapists.push(therapist);

        const therapistAvailabilityIndex = resultMap[daysDifference].availabilityByTherapist.findIndex(
          t => t.therapist.therapistID === therapist.therapistID,
        );

        if (therapistAvailabilityIndex === -1) {
          resultMap[daysDifference].availabilityByTherapist.push({
            therapist,
            therapistName: `${therapist.firstName} ${therapist.lastName}`,
            timeSlots: [slot],
          });
        } else {
          resultMap[daysDifference].availabilityByTherapist[therapistAvailabilityIndex].timeSlots.push(slot);
        }
      });

      resultMap[daysDifference].availableTherapists = Array.from(uniqBy(rawTherapists, d => d.acuityCalendarID));
      resultMap[daysDifference].therapistCount = resultMap[daysDifference].availableTherapists.length;
    }
  });

  //@TODO(juan) refactor and simplify with immer maybe

  /** Sort timeslots */
  let result = resultMap.map(day => ({
    ...day,
    availabilityByTherapist: day.availabilityByTherapist.map(therapist => ({
      ...therapist,
      timeSlots: sortBy(therapist.timeSlots, slot => slot.time),
    })),
  }));

  /** Sort therapist by highest availability */
  result = result.map(
    day =>
      ({
        ...day,
        availabilityByTherapist: sortBy(day.availabilityByTherapist, a => a.timeSlots.length * -1),
      } as EvaluationCalendarDay),
  );

  return result;
};
